"use client"

import {
    Pagination,
    PaginationContent,
    PaginationItem,
    PaginationNext,
    PaginationPrevious,
    PaginationEllipsis,
} from "@/components/ui/shadcn/pagination"
import {Select, SelectContent, SelectItem, SelectTrigger, SelectValue} from '@/components/ui/shadcn/select'

import {useState} from "react"
// import { Button } from "@/components/ui/shadcn/button"

type PaginatedListProps<T> = {
    items: T[]
    itemsPerPage?: number
    renderItem: (item: T, index: number) => React.ReactNode
    wrapperClassName?: string
    itemsPerPageOptions?: number[]
    onPageChange?: (page: number, pageSize: number) => void
    showMeta?: boolean
}

export function PaginatedList<T>({
                                     items,
                                     renderItem,
                                     wrapperClassName = '',
                                     itemsPerPageOptions = [4, 8, 12, -1],
                                     onPageChange,
                                     showMeta = items.length > itemsPerPageOptions[0],
                                 }: PaginatedListProps<T>) {
    const [currentPage, setCurrentPage] = useState(1)
    const [itemsPerPage, setItemsPerPage] = useState(itemsPerPageOptions[0])

    const totalPages = Math.ceil(items.length / itemsPerPage)
    const startIndex = (currentPage - 1) * itemsPerPage
    const paginatedItems = items.slice(startIndex, startIndex + itemsPerPage)

    const showAll = itemsPerPage === -1

    const handlePageChange = (page: number) => {
        setCurrentPage(page)
        onPageChange?.(page, itemsPerPage)
    }

    const handlePageSizeChange = (size: number) => {
        setItemsPerPage(size)
        setCurrentPage(1)
        onPageChange?.(1, size)
    }

    const getPageNumbers = (): (number | 'ellipsis')[] => {
        const pages: (number | 'ellipsis')[] = []

        if (totalPages <= 5) {
            return Array.from({length: totalPages}, (_, i) => i + 1)
        }

        pages.push(1)

        if (currentPage > 3) {
            pages.push('ellipsis')
        }

        const middlePages = [currentPage - 1, currentPage, currentPage + 1].filter(
            (p) => p > 1 && p < totalPages
        )

        pages.push(...middlePages)

        if (currentPage < totalPages - 2) {
            pages.push('ellipsis')
        }

        if (totalPages > 1) {
            pages.push(totalPages)
        }

        return pages
    }


    return (
        <div className="space-y-4">
            {/* TODO: improve meta */}
            {showMeta && (
                <div className="flex flex-wrap justify-between items-center gap-4 text-sm text-muted-foreground">
                    <div>
                        Showing{' '}
                        <strong>
                            {startIndex + 1}–{showAll ? items.length : Math.min(startIndex + itemsPerPage, items.length)}
                        </strong>{' '}
                        of <strong>{items.length}</strong> items
                    </div>
                    <div className="flex items-center gap-2">
                        <span>Items per page:</span>
                        <Select
                            value={itemsPerPage.toString()}
                            onValueChange={(value) => handlePageSizeChange(Number(value))}
                        >
                            <SelectTrigger className="w-20 h-8">
                                <SelectValue/>
                            </SelectTrigger>
                            <SelectContent className="bg-white shadow-lg border border-border" side="bottom">
                                {itemsPerPageOptions.map((opt) => (
                                    <SelectItem key={opt} value={opt.toString()}>
                                        {opt === -1 ? 'All' : opt}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                </div>
            )}

            <div className={wrapperClassName}>
                {paginatedItems.map((item, index) => renderItem(item, index))}
            </div>

            {totalPages > 1 && (
                <div className="flex justify-center mt-4">
                    <Pagination>
                        <PaginationContent>
                            <PaginationItem>
                                <PaginationPrevious
                                    onClick={() => handlePageChange(Math.max(1, currentPage - 1))}
                                    className={currentPage === 1 ? 'pointer-events-none opacity-50' : ''}
                                />
                            </PaginationItem>

                            {getPageNumbers().map((page, idx) =>
                                page === 'ellipsis' ? (
                                    <PaginationItem key={`ellipsis-${idx}`}>
                                        <PaginationEllipsis/>
                                    </PaginationItem>
                                ) : (
                                    <PaginationItem key={page}>
                                        <button
                                            className={`px-3 py-1 text-sm rounded-md ${
                                                currentPage === page
                                                    ? 'bg-primary text-white'
                                                    : 'hover:bg-muted'
                                            }`}
                                            onClick={() => handlePageChange(page)}
                                        >
                                            {page}
                                        </button>
                                    </PaginationItem>
                                )
                            )}

                            <PaginationItem>
                                <PaginationNext
                                    onClick={() => handlePageChange(Math.min(totalPages, currentPage + 1))}
                                    className={currentPage === totalPages ? 'pointer-events-none opacity-50' : ''}
                                />
                            </PaginationItem>
                        </PaginationContent>
                    </Pagination>
                </div>
            )}
        </div>
    )
}
